import React, { useState, MouseEvent, ReactNode } from "react";
import { ArrowRight, Calendar, Award, BookOpen, Users, CheckCircle, Mail, Phone, MapPin, ChevronDown, ChevronUp } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

// ================== TYPES ==================
type TrackName = "Explorer" | "Builder" | "Scaler" | "Women Entrepreneur" | "Impact & ESG";

type WeekItem = {
  week: number;
  theme: string;
  topics: string[];
  assignment: string;
};

type Tracks = Record<TrackName, WeekItem[]>;

type Mentor = { name: string; role: string };

// ================== THEME / HELPERS ==================
interface SectionProps { id?: string; className?: string; children: ReactNode }
const Section: React.FC<SectionProps> = ({ id, className = "", children }) => (
  <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);

const Badge: React.FC<{ children: ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
    {children}
  </span>
);

const Card: React.FC<{ className?: string; children: ReactNode }> = ({ className = "", children }) => (
  <div className={`rounded-2xl shadow-lg bg-white/90 backdrop-blur border border-slate-100 ${className}`}>{children}</div>
);

const H2: React.FC<{ children: ReactNode; kicker?: string }> = ({ children, kicker }) => (
  <div className="mb-8 text-center">
    {kicker && <div className="mb-2 text-sm font-semibold tracking-widest uppercase text-indigo-600">{kicker}</div>}
    <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800">{children}</h2>
  </div>
);

const Pill: React.FC<{ children: ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-slate-900 text-white px-3 py-1 rounded-full">
    {children}
  </span>
);

// ================== DATA ==================
const trackFees: Record<TrackName, number> = {
  Explorer: 15000,
  Builder: 35000,
  Scaler: 50000,
  "Women Entrepreneur": 25000,
  "Impact & ESG": 30000,
};

const tracks: Tracks = {
  Explorer: [
    { week: 1, theme: "Introduction to Entrepreneurship", topics: ["Mindset", "Design thinking"], assignment: "1‑page reflection: Why I want to be an entrepreneur" },
    { week: 2, theme: "Idea Generation & Validation", topics: ["Brainstorming", "Problem–solution fit"], assignment: "Create 3 ideas; validate 1 via 10 interviews" },
    { week: 3, theme: "Business Models 101", topics: ["Business Model Canvas"], assignment: "Draft a simple BMC" },
    { week: 4, theme: "Market & Customer Discovery", topics: ["Target segments", "Personas"], assignment: "Build 2 customer personas" },
    { week: 5, theme: "Basics of Finance", topics: ["Costs", "Pricing", "Revenue"], assignment: "Simple unit economics sheet (Excel)" },
    { week: 6, theme: "Digital Basics", topics: ["Social media", "E‑commerce tools"], assignment: "Create LinkedIn + a Canva poster" },
    { week: 7, theme: "Pitching Skills", topics: ["Storytelling", "Elevator pitch"], assignment: "Record a 90‑sec pitch video" },
    { week: 8, theme: "Demo Day", topics: ["Presentations", "Peer feedback"], assignment: "5‑min final pitch + Q&A" },
  ],
  Builder: [
    { week: 1, theme: "Entrepreneurial Leadership", topics: ["Growth mindset", "Founder roles"], assignment: "Self‑assessment: My founder style" },
    { week: 2, theme: "Business Model & Lean", topics: ["Value proposition", "Pivots"], assignment: "Create/refine BMC + Lean Canvas" },
    { week: 3, theme: "Market Research & Opportunity", topics: ["TAM/SAM/SOM", "Competitors"], assignment: "Competitor benchmarking table" },
    { week: 4, theme: "Go‑to‑Market Strategy", topics: ["Channels", "Positioning"], assignment: "GTM roadmap across 3 channels" },
    { week: 5, theme: "Marketing & Sales", topics: ["Branding", "Digital funnels"], assignment: "Sample ad + funnel sketch" },
    { week: 6, theme: "Finance for Entrepreneurs", topics: ["P&L", "Cash flow"], assignment: "12‑month P&L projection" },
    { week: 7, theme: "Operations & Supply Chain", topics: ["SOPs", "Logistics"], assignment: "Map a simple supply chain" },
    { week: 8, theme: "Legal & Compliance", topics: ["Registrations", "IP basics"], assignment: "Compliance checklist draft" },
    { week: 9, theme: "Digital Enablement", topics: ["ERP/CRM", "ONDC/DPI"], assignment: "Mock online storefront" },
    { week: 10, theme: "Fundraising Basics", topics: ["Sources of capital", "Expectations"], assignment: "10‑slide investor deck" },
    { week: 11, theme: "Ecosystem Building", topics: ["Partnerships", "Networks"], assignment: "Identify 3 partners + MoU outline" },
    { week: 12, theme: "Demo Day", topics: ["Pitch", "Feedback"], assignment: "Final pitch to panel" },
  ],
  Scaler: [
    { week: 1, theme: "Scaling Mindset", topics: ["Leadership", "Delegation"], assignment: "Write a scaling plan" },
    { week: 2, theme: "Advanced Strategy", topics: ["Blue Ocean", "Platform models"], assignment: "Map current vs future strategy" },
    { week: 3, theme: "Financial Planning", topics: ["Forecasting", "Cap tables"], assignment: "3‑year financial model" },
    { week: 4, theme: "Advanced Marketing", topics: ["Omnichannel", "Brand equity"], assignment: "Omnichannel strategy doc" },
    { week: 5, theme: "Operations Scaling", topics: ["Automation", "OKRs"], assignment: "Design 3 OKRs" },
    { week: 6, theme: "Technology Scaling", topics: ["Cloud", "AI"], assignment: "Digital transformation roadmap" },
    { week: 7, theme: "Talent & Culture", topics: ["Hiring", "ESOPs"], assignment: "Org structure + HR policy" },
    { week: 8, theme: "Governance & Compliance", topics: ["Board", "ESG"], assignment: "Governance charter" },
    { week: 9, theme: "Investor Readiness", topics: ["DD", "Valuation"], assignment: "DCF/Comparable valuation" },
    { week: 10, theme: "Deal Structuring", topics: ["Term sheets", "Negotiation"], assignment: "Term sheet review" },
    { week: 11, theme: "Global Expansion", topics: ["Export readiness", "Cross‑border"], assignment: "2 export markets + barriers" },
    { week: 12, theme: "Partnerships", topics: ["Alliances", "JVs"], assignment: "Partnership proposal" },
    { week: 13, theme: "Ecosystem Integration", topics: ["ONDC", "ULIP", "DPI stack"], assignment: "API/partnership plan" },
    { week: 14, theme: "Risk & Resilience", topics: ["Crisis mgmt", "BCP"], assignment: "Risk register (10 risks)" },
    { week: 15, theme: "Final Pitch Prep", topics: ["Storytelling"], assignment: "Refine pitch deck" },
    { week: 16, theme: "Mega Demo Day", topics: ["Investor Pitch"], assignment: "Pitch to VCs/angels/CSR" },
  ],
  "Women Entrepreneur": [
    { week: 1, theme: "Entrepreneurial Mindset", topics: ["Confidence", "Leadership"], assignment: "Reflection: My journey" },
    { week: 2, theme: "Business Model Basics", topics: ["Idea → Business"], assignment: "Draft BMC" },
    { week: 3, theme: "Financial Literacy", topics: ["Budgeting", "Loans"], assignment: "Personal + business budget" },
    { week: 4, theme: "Digital Enablement", topics: ["WhatsApp", "Shopify", "ONDC"], assignment: "Create a digital shopfront" },
    { week: 5, theme: "Branding for Women", topics: ["Storytelling", "Social"], assignment: "Design logo + tagline" },
    { week: 6, theme: "Sales & Negotiation", topics: ["Sales", "Negotiation"], assignment: "Role‑play negotiation" },
    { week: 7, theme: "Market Access", topics: ["Local → Global"], assignment: "Identify 3 new markets" },
    { week: 8, theme: "Funding Access", topics: ["Govt schemes", "CSR"], assignment: "Apply to one scheme" },
    { week: 9, theme: "Work–Life Balance", topics: ["Time mgmt", "Resilience"], assignment: "Weekly planner" },
    { week: 10, theme: "Networking & Sisterhood", topics: ["Peer community"], assignment: "Join 2 networks" },
    { week: 11, theme: "Pitch Readiness", topics: ["Confidence"], assignment: "3‑min pitch" },
    { week: 12, theme: "Graduation Demo Day", topics: ["Showcase"], assignment: "Business showcase + pitch" },
  ],
  "Impact & ESG": [
    { week: 1, theme: "Social Entrepreneurship 101", topics: ["Defining impact"], assignment: "Theory of change" },
    { week: 2, theme: "Impact Models", topics: ["Hybrid/NP/FP"], assignment: "Choose model type" },
    { week: 3, theme: "ESG Basics", topics: ["UN SDGs", "ESG pillars"], assignment: "Map to SDGs" },
    { week: 4, theme: "Impact Measurement", topics: ["Metrics", "SROI"], assignment: "Impact dashboard" },
    { week: 5, theme: "Financing Impact", topics: ["Grants", "CSR", "Blended"], assignment: "3 funding sources" },
    { week: 6, theme: "Marketing for Impact", topics: ["Cause storytelling"], assignment: "Campaign draft" },
    { week: 7, theme: "Climate & Sustainability", topics: ["Carbon", "Circular"], assignment: "Footprint baseline" },
    { week: 8, theme: "Policy & Compliance", topics: ["CSR law", "SEBI ESG"], assignment: "Compliance checklist" },
    { week: 9, theme: "Partnerships", topics: ["NGOs", "Govt", "Corp"], assignment: "Stakeholder map" },
    { week: 10, theme: "Scaling Impact", topics: ["Replication", "Franchising"], assignment: "Scaling plan" },
    { week: 11, theme: "Pitch for Impact", topics: ["Social pitch"], assignment: "Impact pitch deck" },
    { week: 12, theme: "Demo Day", topics: ["Showcase"], assignment: "Pitch to CSR/impact investors" },
  ],
};

const mentors: Mentor[] = [
  { name: "Anita Rao", role: "Investor & Startup Mentor" },
  { name: "Ravi Kumar", role: "Founder, Scaler Coach" },
  { name: "Nisha Verma", role: "Marketing Strategist" },
  { name: "Dev Patel", role: "Finance & Fundraising" },
  { name: "Priya Iyer", role: "Operations & Supply Chain" },
  { name: "Arun Sharma", role: "Digital & AI Enablement" },
];

// ================== DEV SELF‑TESTS ==================
function runSelfTests(): void {
  try {
    console.assert(Array.isArray(tracks.Explorer) && tracks.Explorer.length === 8, "Explorer should have 8 weeks");
    console.assert(Array.isArray(tracks.Builder) && tracks.Builder.length === 12, "Builder should have 12 weeks");
    console.assert(Array.isArray(tracks.Scaler) && tracks.Scaler.length === 16, "Scaler should have 16 weeks");
    console.assert(Array.isArray(tracks["Women Entrepreneur"]) && tracks["Women Entrepreneur"].length === 12, "Women Entrepreneur should have 12 weeks");
    console.assert(Array.isArray(tracks["Impact & ESG"]) && tracks["Impact & ESG"].length === 12, "Impact & ESG should have 12 weeks");
    (Object.keys(tracks) as TrackName[]).forEach(t => console.assert(trackFees[t] > 0, `Fee missing for ${t}`));
  } catch {
    // tests are advisory only
  }
}
runSelfTests();

// ================== UI SUB‑COMPONENTS ==================
const smoothScroll = (id: string) => {
  const el = document.querySelector(id);
  if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
};

const SafeLink: React.FC<{ href: string; children: ReactNode; className?: string; onClick?: (e: MouseEvent<HTMLAnchorElement>) => void }> = ({ href, children, className = "", onClick }) => {
  const isExternal = /^(https?:)?\/\//.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <a href={href} className={className} onClick={(e) => { e.preventDefault(); smoothScroll(href); onClick && onClick(e); }}>
      {children}
    </a>
  );
};

// const Nav: React.FC = () => {
//   const [open, setOpen] = useState(false);
//   const items = [
//     { href: "#home", label: "Home"},
//     { href: "#programs", label: "Programs"},
//     { href: "#curriculum", label: "Curriculum"},
//     { href: "#mentors", label: "Mentors"},
//     { href: "#fees", label: "Fees"},
//     { href: "#admissions", label: "Admissions"},
//     { href: "#faq", label: "FAQ"},
//     { href: "#contact", label: "Contact"},
//   ];
//   return (
//     <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
//       <Section className="flex h-16 items-center justify-between">
//         <SafeLink href="#home" className="flex items-center gap-2 font-extrabold text-slate-900 text-lg">
//           <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-indigo-600 text-white font-black">E</span>
//           EDA
//         </SafeLink>
//         <nav className="hidden md:flex items-center gap-6 text-sm">
//           {items.map(i => (
//             <SafeLink key={i.href} href={i.href} className="text-slate-600 hover:text-slate-900">{i.label}</SafeLink>
//           ))}
//           <SafeLink href="#apply" className="inline-flex items-center gap-2 rounded-full bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700">Apply Now <ArrowRight className="w-4 h-4"/></SafeLink>
//         </nav>
//         <button className="md:hidden p-2" onClick={() => setOpen(v=>!v)} aria-label="Toggle menu">
//           <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
//           <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
//           <span className="block w-6 h-0.5 bg-slate-900"></span>
//         </button>
//       </Section>
//       {open && (
//         <div className="md:hidden border-t border-slate-200 bg-white">
//           <Section className="py-4 flex flex-col gap-3">
//             {items.map(i => (
//               <SafeLink key={i.href} href={i.href} onClick={()=>setOpen(false)} className="text-slate-700">{i.label}</SafeLink>
//             ))}
//             <SafeLink href="#apply" onClick={()=>setOpen(false)} className="inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700">Apply Now <ArrowRight className="w-4 h-4"/></SafeLink>
//           </Section>
//         </div>
//       )}
//     </header>
//   );
// };

const Nav = () => {
  const submenus = [
    // { href: "#home", label: "Home"},
    { href: "#programs", label: "Programs"},
    { href: "#curriculum", label: "Curriculum"},
    { href: "#mentors", label: "Mentors"},
    { href: "#fees", label: "Fees"},
    { href: "#admissions", label: "Admissions"},
    { href: "#faq", label: "FAQ"},
    { href: "#contact", label: "Contact"},
  ];

  const [openMobile, setOpenMobile] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
              <Section id="one-digiit" className="flex flex-row h-12 w-full items-center justify-right">
                {/* Second row: submenus (desktop only) */}
                <div className="hidden md:block flex flex-row w-full items-center justify-right">
                  <Section
                    id="nav-sub"
                    className="flex items-center gap-4 py-2 overflow-x-auto justify-end"
                  >
                    {submenus.map((i) => (
                      <SafeLink
                        key={i.href}
                        href={i.href}
                        className="text-sm text-slate-600 hover:text-slate-900 whitespace-nowrap"
                      >
                        {i.label}
                      </SafeLink>
                    ))}
        
                    {/* CTA */}
                    <SafeLink
                      href="#contact"
                      className="hidden md:inline-flex items-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600"
                    >
                      Talk to Us <ArrowRight className="w-4 h-4" />
                    </SafeLink>
                  </Section>
                </div>
        
        
                {/* Mobile menu button */}
                <button
                  className="md:hidden p-2"
                  onClick={() => setOpenMobile((v) => !v)}
                  aria-label="Toggle menu"
                >
                  <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
                  <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
                  <span className="block w-6 h-0.5 bg-slate-900"></span>
                </button>
              </Section>
        
              {/* Mobile dropdown */}
              {openMobile && (
                <div className="md:hidden border-t border-slate-200 bg-white">
                  <Section id={''} className="py-4 flex flex-col gap-3">
                    <div className="mt-4 border-t border-slate-200 pt-4">
                      {submenus.map((i) => (
                        <SafeLink
                          key={i.href}
                          href={i.href}
                          onClick={() => setOpenMobile(false)}
                          className="text-slate-600"
                        >
                          {i.label}
                        </SafeLink>
                      ))}
                    </div>
                    <SafeLink
                      href="#contact"
                      onClick={() => setOpenMobile(false)}
                      className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600"
                    >
                      Talk to Us <ArrowRight className="w-4 h-4" />
                    </SafeLink>
                  </Section>
                </div>
              )}
            </header>
  );
};

const Hero: React.FC = () => (
  <div id="home" className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-white">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.06),transparent_35%)]"/>
    <Section className="relative py-16 md:py-24">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6}}>
          <div className="flex gap-2 mb-4">
            <Badge>Digital‑First</Badge>
            <Badge>Mentor‑Led</Badge>
            <Badge>Demo Day</Badge>
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-tight">Entrepreneurship Development Academy</h1>
          <p className="mt-4 text-lg md:text-xl text-slate-200 max-w-2xl">Build, scale, and sustain your venture with hands‑on curriculum, expert mentors, and ecosystem access. Tracks for Explorer, Builder, Scaler, Women, and Impact/ESG.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <SafeLink href="#apply" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-5 py-3 font-semibold">Apply Now <ArrowRight className="w-4 h-4"/></SafeLink>
            <SafeLink href="#programs" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-3">Explore Programs</SafeLink>
          </div>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-slate-300">
            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4"/> Cohorts every quarter</span>
            <span className="inline-flex items-center gap-2"><Award className="w-4 h-4"/> EDA Certification</span>
            <span className="inline-flex items-center gap-2"><Users className="w-4 h-4"/> 1:1 Mentorship</span>
          </div>
        </motion.div>
        <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6, delay:0.1}}>
          <Card className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Pill><BookOpen className="w-4 h-4"/> Tracks</Pill>
              <span className="text-slate-500">Choose your path</span>
            </div>
            <ul className="grid sm:grid-cols-2 gap-4">
              {(Object.keys(tracks) as TrackName[]).map(t => (
                <li key={t} className="rounded-xl p-4 border border-slate-200 hover:shadow-md transition">
                  <div className="font-semibold text-slate-900">{t}</div>
                  <div className="mt-1 text-sm text-slate-600">Fee: ₹{trackFees[t].toLocaleString()}</div>
                </li>
              ))}
            </ul>
            <div className="mt-6 text-xs text-slate-500">Scholarships available for Women & Impact tracks. CSR sponsorships possible.</div>
          </Card>
        </motion.div>
      </div>
    </Section>
  </div>
);

const ProgramCards: React.FC = () => (
  <Section id="programs" className="py-16">
    <H2 kicker="Programs">Pick a track that fits your journey</H2>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {(Object.entries(tracks) as [TrackName, WeekItem[]][]).map(([name, weeks]) => (
        <motion.div key={name} initial={{opacity:0, y:12}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.4}}>
          <Card className="p-6 h-full flex flex-col">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">{name}</h3>
              <Badge>{weeks.length} weeks</Badge>
            </div>
            <p className="mt-2 text-slate-600 line-clamp-3">Curated, mentor‑led sessions with weekly projects. Ideal for {name === "Explorer" ? "students & early aspirants" : name === "Builder" ? "first‑time founders & MSMEs" : name === "Scaler" ? "growth‑stage founders" : name === "Women Entrepreneur" ? "women‑led businesses" : "social & climate entrepreneurs"}.</p>
            <div className="mt-4 text-sm text-slate-700">Fee: <span className="font-semibold">₹{trackFees[name].toLocaleString()}</span></div>
            <div className="mt-6 flex gap-3 mt-auto">
              <SafeLink href="#curriculum" className="rounded-full px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-50">View curriculum</SafeLink>
              <SafeLink href="#apply" className="rounded-full px-4 py-2 text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700">Apply</SafeLink>
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  </Section>
);

const WeekRow: React.FC<{ item: WeekItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white/70">
      <button className="w-full flex items-center justify-between text-left" onClick={()=>setOpen(v=>!v)} aria-expanded={open}>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold">{item.week}</div>
          <div>
            <div className="font-semibold text-slate-900">{item.theme}</div>
            <div className="text-xs text-slate-500">{item.topics.join(" • ")}</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-500"/> : <ChevronDown className="w-5 h-5 text-slate-500"/>}
      </button>
      {open && (
        <div className="mt-3 text-sm">
          <div className="text-slate-700"><span className="font-semibold">Assignment:</span> {item.assignment}</div>
        </div>
      )}
    </div>
  );
};

const Curriculum: React.FC = () => {
  const trackNames = Object.keys(tracks) as TrackName[];
  const [active, setActive] = useState<TrackName>(trackNames[0]);
  const weeks = tracks[active];
  return (
    <Section id="curriculum" className="py-16">
      <H2 kicker="Curriculum">Week‑by‑week syllabus & projects</H2>
      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-3 justify-center mb-8">
        {trackNames.map(t => (
          <button key={t} onClick={()=>setActive(t)} className={`px-4 py-2 rounded-full text-sm font-semibold border ${active===t?"bg-indigo-600 text-white border-indigo-600":"border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{t}</button>
        ))}
      </div>
      {/* Weeks */}
      <div className="grid md:grid-cols-2 gap-4">
        {weeks.map(w => <WeekRow key={`${active}-${w.week}`} item={w}/>) }
      </div>
      <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
        <button onClick={()=>window.print()} className="rounded-full px-4 py-2 border border-slate-300 hover:bg-slate-50">Print / Save PDF</button>
        <SafeLink href="#apply" className="rounded-full px-4 py-2 bg-indigo-600 text-white font-semibold hover:bg-indigo-700">Apply to {active}</SafeLink>
      </div>
    </Section>
  );
};

const Mentors: React.FC = () => (
  <Section id="mentors" className="py-16">
    <H2 kicker="Mentors">Learn from operators & experts</H2>
    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
      {mentors.map((m, idx) => (
        <Card key={idx} className="p-6 text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600"/>
          <div className="mt-4 font-semibold text-slate-900">{m.name}</div>
          <div className="text-sm text-slate-500">{m.role}</div>
        </Card>
      ))}
    </div>
  </Section>
);

const Fees: React.FC = () => (
  <Section id="fees" className="py-16">
    <H2 kicker="Fees & Scholarships">Transparent pricing, generous scholarships</H2>
    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
      {(Object.entries(trackFees) as [TrackName, number][]).map(([name, fee]) => (
        <Card key={name} className="p-6 text-center">
          <div className="text-sm uppercase tracking-wider text-slate-500">{name}</div>
          <div className="mt-2 text-3xl font-extrabold text-slate-900">₹{fee.toLocaleString()}</div>
          <div className="mt-2 text-xs text-slate-500">EMI & CSR sponsorships available</div>
          <SafeLink href="#apply" className="mt-5 inline-flex items-center justify-center rounded-full px-4 py-2 bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700">Apply</SafeLink>
        </Card>
      ))}
    </div>
  </Section>
);

const Admissions: React.FC = () => (
  <Section id="admissions" className="py-16">
    <H2 kicker="Admissions">Simple, founder‑friendly process</H2>
    <div className="grid md:grid-cols-5 gap-4">
      {[
        {step:1, title:"Apply", desc:"Submit the form with basic details & chosen track"},
        {step:2, title:"Shortlist", desc:"Profile review by program team"},
        {step:3, title:"Interview", desc:"15‑min fit & goals discussion"},
        {step:4, title:"Offer", desc:"Admission + scholarship decision"},
        {step:5, title:"Enroll", desc:"Complete payment & onboarding"},
      ].map(s => (
        <Card key={s.step} className="p-5 text-center">
          <div className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-indigo-600 text-white font-bold">{s.step}</div>
          <div className="mt-3 font-semibold text-slate-900">{s.title}</div>
          <div className="text-sm text-slate-600">{s.desc}</div>
        </Card>
      ))}
    </div>
    <div className="mt-6 text-center">
      <SafeLink href="#apply" className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-5 py-3 font-semibold">Start Application <ArrowRight className="w-4 h-4"/></SafeLink>
    </div>
  </Section>
);

const FAQ: React.FC = () => (
  <Section id="faq" className="py-16">
    <H2 kicker="FAQ">Answers to common questions</H2>
    <div className="grid md:grid-cols-2 gap-4">
      {[ 
        {q: "Are sessions online or offline?", a: "Blended: live online classes, optional city meetups, and an on‑site Demo Day (varies by cohort)."},
        {q: "Do you offer scholarships?", a: "Yes—especially for Women and Impact tracks via CSR partners. Need‑based aid also available."},
        {q: "Will I get a certificate?", a: "Yes, you’ll receive an EDA Certificate for your specific track on successful completion."},
        {q: "How much time commitment?", a: "~4–6 hours/week including live sessions, projects, and mentorship."},
      ].map((f, i) => (
        <Card key={i} className="p-5">
          <div className="font-semibold text-slate-900">{f.q}</div>
          <div className="text-sm text-slate-600 mt-2">{f.a}</div>
        </Card>
      ))}
    </div>
  </Section>
);

const ContactApply: React.FC = () => {
  const [form, setForm] = useState({ name:"", email:"", phone:"", city:"", track:"Explorer" as TrackName, background:"", message:"" });
  const [submitted, setSubmitted] = useState(false);
  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if(!form.name || !form.email) { alert("Please fill name & email"); return; }
    setSubmitted(true);
    // TODO: Integrate Formspree / Netlify forms / custom API endpoint
  };

  return (
    <div id="contact" className="bg-gradient-to-b from-slate-50 to-white border-t border-slate-200">
      <Section className="py-16">
        <H2 kicker="Contact & Apply">We'd love to hear from you</H2>
        <div className="grid lg:grid-cols-3 gap-8">
          <Card className="p-6 lg:col-span-2">
            <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Full name</label>
                <input name="name" value={form.name} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Your name"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Email</label>
                <input name="email" value={form.email} onChange={onChange} type="email" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="you@email.com"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Phone</label>
                <input name="phone" value={form.phone} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="+91‑"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">City</label>
                <input name="city" value={form.city} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Your city"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Track</label>
                <select name="track" value={form.track} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {(Object.keys(tracks) as TrackName[]).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-600">Background</label>
                <input name="background" value={form.background} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Student / MSME / Founder..."/>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm text-slate-600">Message</label>
                <textarea name="message" value={form.message} onChange={onChange} rows={4} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Tell us about your goals"></textarea>
              </div>
              <div id="apply" className="sm:col-span-2 flex flex-wrap gap-3 items-center">
                <button className="rounded-full bg-indigo-600 text-white px-5 py-3 font-semibold hover:bg-indigo-700" type="submit">Submit Application</button>
                {submitted && <span className="text-sm text-green-600 inline-flex items-center gap-2"><CheckCircle className="w-4 h-4"/> Submitted! We will get back to you soon.</span>}
              </div>
            </form>
          </Card>
          <div className="space-y-4">
            <Card className="p-6">
              <div className="font-semibold text-slate-900">Reach us</div>
              <div className="mt-3 text-sm text-slate-600 flex flex-col gap-2">
                <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4"/> hello@eda.example</span>
                <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4"/> +91‑90000 00000</span>
                <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4"/> Coimbatore • Chennai • Bengaluru</span>
              </div>
            </Card>
            <Card className="p-6">
              <div className="font-semibold text-slate-900">Download Brochure</div>
              <div className="mt-2 text-sm text-slate-600">Use your browser's Print → Save as PDF for a quick brochure of the curriculum and tracks.</div>
              <button onClick={()=>window.print()} className="mt-4 rounded-full px-4 py-2 border border-slate-300 hover:bg-slate-50 text-sm font-semibold">Print / Save PDF</button>
            </Card>
          </div>
        </div>
      </Section>
    </div>
  );
};

const LegalSections: React.FC = () => (
  <>
    <Section id="terms" className="py-16">
      <H2 kicker="Legal">Terms & Conditions</H2>
      <div className="max-w-3xl mx-auto text-slate-700 text-sm space-y-4">
        <p>Use this section to describe your terms of service, user obligations, payment & refund policy, IP ownership, disclaimers, and governing law.</p>
      </div>
    </Section>
    <Section id="privacy" className="py-16">
      <H2 kicker="Legal">Privacy Policy</H2>
      <div className="max-w-3xl mx-auto text-slate-700 text-sm space-y-4">
        <p>Explain what data you collect, why, how it’s used, data retention, third‑party processors, cookies, user rights, and contact information for privacy requests.</p>
      </div>
    </Section>
  </>
);

const Footer: React.FC = () => (
  <footer className="bg-slate-900 text-slate-300">
    <Section className="py-10">
      <div className="grid md:grid-cols-3 gap-8 items-center">
        <div>
          <div className="flex items-center gap-2 font-extrabold text-white text-lg">
            <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-white text-slate-900 font-black">E</span>
            EDA
          </div>
          <p className="mt-3 text-sm text-slate-400 max-w-sm">Empowering entrepreneurs to build, scale, and sustain digital‑first businesses.</p>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-white">Quick Links</div>
          <ul className="mt-2 space-y-1">
            <li><SafeLink href="#programs" className="hover:text-white">Programs</SafeLink></li>
            <li><SafeLink href="#curriculum" className="hover:text-white">Curriculum</SafeLink></li>
            <li><SafeLink href="#fees" className="hover:text-white">Fees</SafeLink></li>
            <li><SafeLink href="#admissions" className="hover:text-white">Admissions</SafeLink></li>
            <li><SafeLink href="#terms" className="hover:text-white">Terms</SafeLink></li>
            <li><SafeLink href="#privacy" className="hover:text-white">Privacy</SafeLink></li>
          </ul>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-white">Legal</div>
          <ul className="mt-2 space-y-1">
            <li><SafeLink href="#terms" className="hover:text-white">Terms</SafeLink></li>
            <li><SafeLink href="#privacy" className="hover:text-white">Privacy</SafeLink></li>
          </ul>
        </div>
      </div>
      <div className="mt-8 border-t border-white/10 pt-6 text-xs text-slate-500">© {new Date().getFullYear()} Entrepreneurship Development Academy. All rights reserved.</div>
    </Section>
  </footer>
);

export default function Eda(): JSX.Element {
  return (
    <div className="min-h-screen selection:bg-indigo-200 selection:text-slate-900">
      <Nav/>
      <Hero/>
      <ProgramCards/>
      <Curriculum/>
      <Mentors/>
      <Fees/>
      <Admissions/>
      <FAQ/>
      <ContactApply/>
      <LegalSections/>
      <Footer/>
    </div>
  );
}
